import PlanningDashboard from './planningView/PlanningDashboard';
import { useParams, useSearchParams, useLocation } from 'react-router';
import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_IAP_PARAMS,
  validateAndCreateIapSearchParams,
  type IapParams,
} from 'utils/iapParamValidation';
import { incrementParamsForUI } from './utils/IapUtils';
import { useIapParams } from './hooks/useIapParams';
import { useICSContext } from './planningView/ICSContext';

const IAPDashboardNew = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Handle AI citation navigation - apply targetPhase from context
  const { targetPhase, setTargetPhase, mode, setMode } = useICSContext();

  const storageKey = `iap-dashboard-url-${incidentId}`;
  const savedParams = useIapParams(incidentId || '');

  const getValues = useCallback((): IapParams => {
    return savedParams || DEFAULT_IAP_PARAMS;
  }, [savedParams]);

  const [phase, setPhase] = useState(() => getValues().phase ?? 0);

  const updatePhase = useCallback(
    (newPhase: number) => {
      setPhase(newPhase);
      const uiSearchParams = validateAndCreateIapSearchParams(
        incrementParamsForUI({ mode, phase: newPhase }),
        searchParams
      );
      setSearchParams(uiSearchParams);

      const codeSearchParams = validateAndCreateIapSearchParams(
        { mode, phase: newPhase },
        searchParams
      );

      if (incidentId) {
        const newUrl = location.pathname + '?' + codeSearchParams.toString();
        localStorage.setItem(storageKey, newUrl);
      }
    },
    [
      setPhase,
      mode,
      searchParams,
      setSearchParams,
      incidentId,
      location.pathname,
      storageKey,
    ]
  );

  const updateMode = useCallback(
    (newMode: string) => {
      setMode(newMode);
      const uiSearchParams = validateAndCreateIapSearchParams(
        incrementParamsForUI({ mode: newMode, phase }),
        searchParams
      );
      setSearchParams(uiSearchParams);

      const codeSearchParams = validateAndCreateIapSearchParams(
        { mode: newMode, phase },
        searchParams
      );

      if (incidentId) {
        const newUrl = location.pathname + '?' + codeSearchParams.toString();
        localStorage.setItem(storageKey, newUrl);
      }
    },
    [
      setMode,
      phase,
      searchParams,
      setSearchParams,
      incidentId,
      location.pathname,
      storageKey,
    ]
  );

  useEffect(() => {
    setMode(getValues().mode);
    updateMode(mode);
  }, [getValues, mode, setMode, updateMode]);

  const updateStateFromUrl = useCallback(() => {
    if (incidentId) {
      const initialValues = getValues();
      setMode(initialValues.mode);
      setPhase(initialValues.phase ?? 0);
    }
  }, [incidentId, getValues, setMode, setPhase]);

  useEffect(() => {
    updateStateFromUrl();
  }, [updateStateFromUrl]);

  useEffect(() => {
    if (targetPhase !== null) {
      updatePhase(targetPhase);
      setTargetPhase(null); // Clear after applying to prevent stale navigation
    }
  }, [targetPhase, setTargetPhase, updatePhase]);

  return (
    <PlanningDashboard
      updatePhase={updatePhase}
      updateMode={updateMode}
      currentPhase={phase}
      currentMode={mode}
    />
  );
};

export default IAPDashboardNew;
